// App-wide user feedback: toasts, confirm dialogs, and a text prompt.
//
//   const toast = useToast();
//   toast.success("Saved");
//   api.post(...).catch((err) => toast.apiError(err, "Could not save."));
//
//   const { confirm, prompt } = useDialogs();
//   if (!(await confirm({ title: "Void payroll run?", message: "...", danger: true }))) return;
//   const reason = await prompt({ title: "Reject request", label: "Reason", required: true });
//
// Replaces window.alert / window.confirm / window.prompt, and the per-card
// error banners that scroll out of view on long forms. <FeedbackProvider> is
// mounted once in App.js.
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, Info, XCircle, X } from "lucide-react";

// Modals elsewhere in the app sit at 1000/1100.
const Z_DIALOG = 2100;
const Z_TOAST = 2200;
const MAX_TOASTS = 5;

const KINDS = {
  success: { title: "Success", color: "#15803d", tint: "#dcfce7", Icon: CheckCircle2, duration: 4000, role: "status" },
  info: { title: "Info", color: "#1d4ed8", tint: "#dbeafe", Icon: Info, duration: 5000, role: "status" },
  warning: { title: "Warning", color: "#b45309", tint: "#fef3c7", Icon: AlertTriangle, duration: 8000, role: "alert" },
  error: { title: "Something went wrong", color: "#b91c1c", tint: "#fee2e2", Icon: XCircle, duration: 10000, role: "alert" },
};

const FONT = "'DM Sans', sans-serif";

// Turn whatever an API call threw into one readable sentence. Handles the
// `{error: "..."}` shape this backend uses, DRF's `{detail}` and
// `{field: ["msg"]}` shapes, and network failures.
export function apiErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (typeof err === "string") return err;
  const data = err?.response?.data;
  if (!err?.response && err?.request) return "Network error - please check your connection and try again.";
  if (typeof data === "string" && data.length < 300 && !data.trim().startsWith("<")) return data;
  if (data && typeof data === "object") {
    if (typeof data.error === "string") return data.error;
    if (typeof data.detail === "string") return data.detail;
    const parts = [];
    const walk = (value, key) => {
      if (typeof value === "string") parts.push(key && !/^\d+$/.test(key) && key !== "non_field_errors" ? `${key.replace(/_/g, " ")}: ${value}` : value);
      else if (Array.isArray(value)) value.forEach((v) => walk(v, key));
      else if (value && typeof value === "object") Object.entries(value).forEach(([k, v]) => walk(v, k));
    };
    walk(data, "");
    if (parts.length) return parts.slice(0, 3).join(" ");
  }
  return fallback;
}

// In-form validation message ("Name is required"). Unlike a plain banner it
// scrolls itself into view when it appears, so a user who submitted from the
// bottom of a long form still sees it. Server/network failures belong in a
// toast instead (toast.apiError).
export function InlineAlert({ message, style, scrollKey }) {
  const ref = useRef(null);
  // `scrollKey` (any changing value, e.g. a counter) re-scrolls to an unchanged
  // message, for a form whose second failed submit produces the same text.
  useEffect(() => {
    if (message) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [message, scrollKey]);
  if (!message) return null;
  return <div ref={ref} role="alert" style={style}>{message}</div>;
}

// Error message shown directly under the field it belongs to. Render it as a
// direct child of the wrapper that carries data-field="<name>"; the control
// inside that wrapper turns red via the CSS above.
export function FieldError({ message }) {
  if (!message) return null;
  return (
    <div className="fb-field-error" role="alert" style={{ color: "#b91c1c", fontSize: 12, lineHeight: 1.35, marginTop: 4, fontFamily: FONT }}>
      {message}
    </div>
  );
}

// Field-level validation for a form.
//
//   const fe = useFieldErrors();
//   <div {...fe.rootProps}>                         // clears a field's error as soon as it is edited
//     <div data-field="name">
//       <input .../>
//       <FieldError message={fe.errors.name} />
//     </div>
//   </div>
//   fe.set({ name: "Name is required.", phone: "Enter a phone number." });  // all at once
//   fe.fail("name", "Name is required.");                                   // one
//   fe.clear();                                                             // at the start of submit
//
// On failure the page scrolls to the first invalid field and focuses it.
export function useFieldErrors() {
  const [errors, setErrors] = useState({});
  const [tick, setTick] = useState(0);
  const focusKey = useRef(null);

  const set = useCallback((next) => {
    const clean = {};
    Object.entries(next || {}).forEach(([k, m]) => { if (m) clean[k] = m; });
    setErrors(clean);
    focusKey.current = Object.keys(clean);
    setTick((t) => t + 1);
  }, []);

  const fail = useCallback((field, message) => set({ [field]: message }), [set]);

  const clear = useCallback((field) => {
    setErrors((cur) => {
      if (!field) return Object.keys(cur).length ? {} : cur;
      if (!(field in cur)) return cur;
      const rest = { ...cur };
      delete rest[field];
      return rest;
    });
  }, []);

  useEffect(() => {
    const keys = focusKey.current;
    if (!keys || !keys.length) return;
    focusKey.current = null;
    // Focus the field that is first on the page, not first in the validation order.
    const els = keys.map((k) => document.querySelector(`[data-field="${k}"]`)).filter(Boolean);
    els.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    const el = els[0];
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const ctl = el.matches("input, select, textarea") ? el : el.querySelector("input:not([type=hidden]), select, textarea");
    ctl?.focus({ preventScroll: true });
  }, [tick]);

  // Keep a row of fields level when one of them grows an error message.
  //
  // In a bottom-aligned flex row, the field that gains a message grows taller
  // and its input is pushed up while its neighbours stay put. So every other
  // child of that row reserves the same extra space (padding for other fields,
  // margin for buttons and the like); inputs stay level and the row, now
  // taller, pushes whatever is below it down. Stacked (non-row) fields need
  // nothing: the message simply takes space in the flow.
  const touched = useRef(new Map());
  useLayoutEffect(() => {
    const restore = () => {
      touched.current.forEach((orig, el) => {
        el.style.paddingBottom = orig.pb;
        el.style.marginBottom = orig.mb;
      });
      touched.current.clear();
    };
    const apply = () => {
      restore();
      const parents = new Set();
      Object.keys(errors).forEach((k) => {
        const w = document.querySelector(`[data-field="${k}"]`);
        if (w && w.parentElement) parents.add(w.parentElement);
      });
      parents.forEach((parent) => {
        const cs = getComputedStyle(parent);
        if (!cs.display.includes("flex") || !cs.flexDirection.startsWith("row")) return;
        const kids = Array.from(parent.children);
        const errHeight = (kid) => {
          const e = kid.matches("[data-field]") ? kid.querySelector(":scope > .fb-field-error") : null;
          return e ? e.offsetHeight + 4 : 0;
        };
        const tallest = Math.max(0, ...kids.map(errHeight));
        if (!tallest) return;
        // Measure before touching anything, then write.
        const plan = kids.map((kid) => {
          const kcs = getComputedStyle(kid);
          return { kid, own: errHeight(kid), pb: parseFloat(kcs.paddingBottom) || 0, mb: parseFloat(kcs.marginBottom) || 0 };
        });
        plan.forEach(({ kid, own, pb, mb }) => {
          touched.current.set(kid, { pb: kid.style.paddingBottom, mb: kid.style.marginBottom });
          if (kid.matches("[data-field]")) kid.style.paddingBottom = `${pb + (tallest - own)}px`;
          else kid.style.marginBottom = `${mb + tallest}px`;
        });
      });
    };
    apply();
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, [errors]);
  useEffect(() => () => {
    touched.current.forEach((orig, el) => { el.style.paddingBottom = orig.pb; el.style.marginBottom = orig.mb; });
    touched.current.clear();
  }, []);

  const rootProps = useMemo(() => {
    const onEdit = (e) => {
      const f = e.target?.closest?.("[data-field]");
      if (f) clear(f.getAttribute("data-field"));
    };
    return { onInputCapture: onEdit, onChangeCapture: onEdit, onClickCapture: onEdit };
  }, [clear]);

  return { errors, set, fail, clear, rootProps };
}

const ToastContext = createContext(null);
const DialogContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <FeedbackProvider>");
  return ctx;
}

export function useDialogs() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialogs must be used inside <FeedbackProvider>");
  return ctx;
}

const CSS = `
@keyframes fb-in { from { opacity: 0; transform: translateX(24px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes fb-progress { from { transform: scaleX(1); } to { transform: scaleX(0); } }
@keyframes fb-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes fb-pop { from { opacity: 0; transform: scale(.97); } to { opacity: 1; transform: none; } }
[data-field]:has(> .fb-field-error) input,
[data-field]:has(> .fb-field-error) select,
[data-field]:has(> .fb-field-error) textarea,
[data-field]:has(> .fb-field-error) > input,
[data-field]:has(> .fb-field-error) > select,
[data-field]:has(> .fb-field-error) > textarea { border-color: #dc2626 !important; box-shadow: 0 0 0 3px rgba(220,38,38,.12) !important; outline: none; }
[data-field][data-group]:has(> .fb-field-error) { outline: 1px dashed #dc2626; outline-offset: 5px; border-radius: 8px; }
@media (prefers-reduced-motion: reduce) { .fb-anim { animation-duration: .001ms !important; } }
`;

function Toast({ toast, onClose }) {
  const { kind, message, title, duration } = toast;
  const k = KINDS[kind];
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const startedAt = useRef(0);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // The timer closes the toast; the progress bar is only a visual that
  // pauses in step with it while the pointer is over the toast.
  useEffect(() => {
    if (!duration || paused) return undefined;
    startedAt.current = Date.now();
    const t = setTimeout(() => closeRef.current(), remaining.current);
    return () => {
      clearTimeout(t);
      remaining.current = Math.max(300, remaining.current - (Date.now() - startedAt.current));
    };
  }, [duration, paused]);

  return (
    <div
      className="fb-anim"
      role={k.role}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: "relative", overflow: "hidden", display: "flex", alignItems: "flex-start", gap: 12, pointerEvents: "auto",
        background: "white", border: "1px solid #e3e9f3", borderRadius: 12, padding: "12px 12px 14px 12px",
        boxShadow: "0 10px 30px rgba(15,30,60,.16), 0 2px 6px rgba(15,30,60,.08)",
        color: "#1e3050", fontFamily: FONT, animation: "fb-in .22s cubic-bezier(.2,.8,.2,1)",
      }}
    >
      <span style={{
        flexShrink: 0, width: 30, height: 30, borderRadius: "50%", background: k.tint,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <k.Icon size={17} color={k.color} />
      </span>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e3050", lineHeight: 1.3 }}>{title || k.title}</div>
        <div style={{ marginTop: 2, fontSize: 12.5, lineHeight: 1.45, color: "#4a6080", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{message}</div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4, borderRadius: 6, color: "#8a99b3", display: "flex", marginTop: -2 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f4f9"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      >
        <X size={15} />
      </button>
      {duration > 0 && (
        <span
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: k.color, opacity: 0.55,
            transformOrigin: "left", animation: `fb-progress ${duration}ms linear forwards`,
            animationPlayState: paused ? "paused" : "running",
          }}
        />
      )}
    </div>
  );
}

function DialogShell({ children, onCancel, labelledBy }) {
  const ref = useRef(null);

  // Escape cancels; Tab stays inside the dialog; focus returns to whatever
  // opened it.
  useEffect(() => {
    const opener = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onCancel(); return; }
      if (e.key !== "Tab" || !ref.current) return;
      const f = ref.current.querySelectorAll("button, input, textarea, [tabindex]:not([tabindex='-1'])");
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [onCancel]);

  return (
    <div
      className="fb-anim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: Z_DIALOG, background: "rgba(15,30,60,.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16, animation: "fb-fade .15s ease-out",
      }}
    >
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="fb-anim"
        style={{
          width: "100%", maxWidth: 420, background: "white", borderRadius: 14, padding: 20,
          boxShadow: "0 20px 50px rgba(15,30,60,.3)", fontFamily: FONT, animation: "fb-pop .15s ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
}

const btn = (variant) => ({
  padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
  border: variant === "cancel" ? "1px solid #d1d9e6" : "1px solid transparent",
  background: variant === "cancel" ? "white" : variant === "danger" ? "#dc2626" : "#1e3a8a",
  color: variant === "cancel" ? "#4a6080" : "white",
});

function ConfirmDialog({ opts, onResult }) {
  const { title, message, confirmLabel, cancelLabel, danger } = opts;
  const cancelRef = useRef(null);
  const okRef = useRef(null);
  useEffect(() => { (danger ? cancelRef : okRef).current?.focus(); }, [danger]);
  return (
    <DialogShell onCancel={() => onResult(false)} labelledBy="fb-dialog-title">
      <div style={{ display: "flex", gap: 12 }}>
        {danger && <AlertTriangle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />}
        <div style={{ minWidth: 0 }}>
          <div id="fb-dialog-title" style={{ fontSize: 15, fontWeight: 700, color: "#1e3050" }}>{title}</div>
          {message && <div style={{ marginTop: 6, fontSize: 13, color: "#4a6080", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{message}</div>}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button ref={cancelRef} type="button" style={btn("cancel")} onClick={() => onResult(false)}>{cancelLabel}</button>
        <button ref={okRef} type="button" style={btn(danger ? "danger" : "primary")} onClick={() => onResult(true)}>{confirmLabel}</button>
      </div>
    </DialogShell>
  );
}

function PromptDialog({ opts, onResult }) {
  const { title, message, label, placeholder, initialValue, required, multiline, confirmLabel, cancelLabel, danger } = opts;
  const [value, setValue] = useState(initialValue || "");
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const invalid = required && !value.trim();
  const submit = () => {
    if (invalid) { setTouched(true); inputRef.current?.focus(); return; }
    onResult(value.trim());
  };
  const field = {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, fontSize: 13, fontFamily: FONT,
    border: `1px solid ${touched && invalid ? "#dc2626" : "#d1d9e6"}`, color: "#1e3050", outline: "none",
  };
  return (
    <DialogShell onCancel={() => onResult(null)} labelledBy="fb-dialog-title">
      <div id="fb-dialog-title" style={{ fontSize: 15, fontWeight: 700, color: "#1e3050" }}>{title}</div>
      {message && <div style={{ marginTop: 6, fontSize: 13, color: "#4a6080", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{message}</div>}
      <label style={{ display: "block", marginTop: 14, fontSize: 12, fontWeight: 600, color: "#4a6080" }}>
        {label}
        {multiline ? (
          <textarea ref={inputRef} rows={3} value={value} placeholder={placeholder} style={{ ...field, marginTop: 4, resize: "vertical" }}
            onChange={(e) => setValue(e.target.value)} />
        ) : (
          <input ref={inputRef} value={value} placeholder={placeholder} style={{ ...field, marginTop: 4 }}
            onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
        )}
      </label>
      {touched && invalid && <div role="alert" style={{ marginTop: 6, fontSize: 12, color: "#b91c1c" }}>This is required.</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button type="button" style={btn("cancel")} onClick={() => onResult(null)}>{cancelLabel}</button>
        <button type="button" style={btn(danger ? "danger" : "primary")} onClick={submit}>{confirmLabel}</button>
      </div>
    </DialogShell>
  );
}

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null); // { type, opts, resolve }
  const nextId = useRef(1);
  const dialogRef = useRef(null);
  dialogRef.current = dialog;

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const show = useCallback((kind, message, options = {}) => {
    const text = String(message ?? "");
    if (!text) return;
    setToasts((list) => {
      // The same message fired twice (double-click, retry loop) shows once.
      const rest = list.filter((t) => !(t.kind === kind && t.message === text));
      const t = { id: nextId.current++, kind, message: text, title: options.title, duration: options.duration ?? KINDS[kind].duration };
      return [...rest, t].slice(-MAX_TOASTS);
    });
  }, []);

  const toast = useMemo(() => ({
    success: (m, o) => show("success", m, o),
    info: (m, o) => show("info", m, o),
    warning: (m, o) => show("warning", m, o),
    error: (m, o) => show("error", m, o),
    apiError: (err, fallback, o) => show("error", apiErrorMessage(err, fallback), o),
    dismiss,
  }), [show, dismiss]);

  const open = useCallback((type, opts) => new Promise((resolve) => {
    // Only one dialog at a time; a newer one cancels the older.
    dialogRef.current?.resolve(dialogRef.current.type === "prompt" ? null : false);
    setDialog({ type, opts, resolve });
  }), []);

  const dialogs = useMemo(() => ({
    confirm: (o) => {
      const opts = typeof o === "string" ? { title: o } : o;
      return open("confirm", { confirmLabel: "Confirm", cancelLabel: "Cancel", ...opts, ...(opts.danger && !opts.confirmLabel ? { confirmLabel: "Delete" } : {}) });
    },
    prompt: (o) => open("prompt", { label: "", confirmLabel: "Submit", cancelLabel: "Cancel", ...o }),
  }), [open]);

  const settle = useCallback((result) => {
    const d = dialogRef.current;
    setDialog(null);
    d?.resolve(result);
  }, []);

  // Don't leave a caller awaiting forever if the provider unmounts.
  useEffect(() => () => dialogRef.current?.resolve(dialogRef.current.type === "prompt" ? null : false), []);

  return (
    <ToastContext.Provider value={toast}>
      <DialogContext.Provider value={dialogs}>
        {children}
        {createPortal(
          <>
            <style>{CSS}</style>
            <div
              style={{
                position: "fixed", top: 64, right: 16, left: 16, marginLeft: "auto", maxWidth: 390, zIndex: Z_TOAST,
                display: "flex", flexDirection: "column", gap: 10, pointerEvents: "none",
              }}
            >
              {toasts.map((t) => <Toast key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
            </div>
            {dialog?.type === "confirm" && <ConfirmDialog opts={dialog.opts} onResult={settle} />}
            {dialog?.type === "prompt" && <PromptDialog opts={dialog.opts} onResult={settle} />}
          </>,
          document.body
        )}
      </DialogContext.Provider>
    </ToastContext.Provider>
  );
}
